import {CommonModule} from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {GateweeInputV2Component} from '../../v2/gatewee-input-v2/gatewee-input-v2.component';
import {GateweeAssetUploadComponent} from '../../v2/gatewee-asset-upload/gatewee-asset-upload.component';
import {GateweeButtonComponent} from '../gatewee-button/gatewee-button.component';

@Component({
  selector: 'gatewee-page-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeInputV2Component,
    GateweeAssetUploadComponent,
    GateweeButtonComponent,
  ],
  templateUrl: './gatewee-page-settings.component.html',
  styleUrls: ['./gatewee-page-settings.component.css'],
})
export class GateweePageSettingsComponent implements OnInit, OnChanges {
  @Input() formGroup?: FormGroup;
  @Input() defaultEmail?: string;
  @Input() defaultWhatsapp?: string;
  @Input() defaultWebsite?: string;
  @Input() domainId?: number;

  private fb = new FormBuilder();
  private _formGroup!: FormGroup;

  get internalFormGroup(): FormGroup {
    return this.formGroup || this._formGroup;
  }

  /** Form group for mobile.hero (title, description, image). */
  get mobileHeroFormGroup(): FormGroup {
    const mobile = this.internalFormGroup.get('mobile') as FormGroup | null;
    return (mobile?.get('hero') as FormGroup) || this.fb.group({});
  }

  get mobileHeroImageControl(): FormControl<string | null> {
    return this.mobileHeroFormGroup.get('image') as FormControl<string | null>;
  }

  get footerFormGroup(): FormGroup {
    return (
      (this.internalFormGroup.get('footer') as FormGroup) || this.fb.group({})
    );
  }

  get assetsFormGroup(): FormGroup {
    return (
      (this.internalFormGroup.get('assets') as FormGroup) || this.fb.group({})
    );
  }

  get assetsTermsOfServiceControl(): FormControl<string | null> {
    return this.assetsFormGroup.get('termsOfService') as FormControl<
      string | null
    >;
  }

  get assetsPrivacyPolicyControl(): FormControl<string | null> {
    return this.assetsFormGroup.get('privacyPolicy') as FormControl<
      string | null
    >;
  }

  get onboardingFormGroup(): FormGroup {
    return (
      (this.internalFormGroup.get('onboarding') as FormGroup) ||
      this.fb.group({})
    );
  }

  get onboardingSidePanelFormGroup(): FormGroup {
    return (
      (this.onboardingFormGroup.get('sidePanel') as FormGroup) ||
      this.fb.group({})
    );
  }

  get sidePanelBackgroundImageControl(): FormControl<string | null> {
    return this.onboardingSidePanelFormGroup.get(
      'backgroundImage',
    ) as FormControl<string | null>;
  }

  get checkoutFormGroup(): FormGroup {
    return (
      (this.internalFormGroup.get('checkout') as FormGroup) || this.fb.group({})
    );
  }

  get checkoutFooterFormGroup(): FormGroup {
    return (
      (this.checkoutFormGroup.get('footer') as FormGroup) || this.fb.group({})
    );
  }

  /** Login page (recipient/domain login monolith). */
  get loginSettingsFormGroup(): FormGroup {
    return (
      (this.internalFormGroup.get('login') as FormGroup) || this.fb.group({})
    );
  }

  get loginSidePanelFormGroup(): FormGroup {
    return (
      (this.loginSettingsFormGroup.get('sidePanel') as FormGroup) ||
      this.fb.group({})
    );
  }

  get loginSidePanelBackgroundImageControl(): FormControl<string | null> {
    return this.loginSidePanelFormGroup.get('backgroundImage') as FormControl<
      string | null
    >;
  }

  get loginSidePanelHeadlineControl(): FormControl<string | null> {
    return this.loginSidePanelFormGroup.get('headline') as FormControl<
      string | null
    >;
  }

  get loginMobileAppFormGroup(): FormGroup {
    return (
      (this.loginSettingsFormGroup.get('mobileApp') as FormGroup) ||
      this.fb.group({})
    );
  }

  get loginStatsFormArray(): FormArray<FormGroup> {
    const stats = this.loginSidePanelFormGroup.get('stats') as
      | FormArray<FormGroup>
      | undefined;
    return stats ?? this.fb.array<FormGroup>([]);
  }

  addLoginStatRow(): void {
    this.loginStatsFormArray.push(
      this.fb.group({
        value: [''],
        label: [''],
      }),
    );
  }

  removeLoginStatRow(index: number): void {
    this.loginStatsFormArray.removeAt(index);
  }

  ngOnInit(): void {
    // Initialize formGroup - either use provided one or create default
    this.initializeFormGroup();
    this.ensureAllNestedGroups();
    this.setDefaultValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle formGroup being set after initialization (when loaded dynamically)
    if (changes['formGroup']) {
      if (this.formGroup && !this._formGroup) {
        this.ensureAllNestedGroups();
        this.setDefaultValues();
      } else if (this.formGroup && this._formGroup) {
        this.ensureAllNestedGroups();
        this.setDefaultValues();
      } else if (!this.formGroup && !this._formGroup) {
        this.initializeFormGroup();
        this.ensureAllNestedGroups();
        this.setDefaultValues();
      }
    }
  }

  private initializeFormGroup(): void {
    // Only create default formGroup if none is provided and we haven't created one yet
    if (!this.formGroup && !this._formGroup) {
      this._formGroup = this.fb.group({
        title: ['', [Validators.required]],
        description: [''],
        keywords: [''],
        googleAnalyticsId: [''],
        supportContactEmail: [''],
        supportContactWhatsapp: [''],
        institutionalWebsiteURL: [''],
        footer: this.fb.group({
          copyright: [''],
        }),
        assets: this.fb.group({
          termsOfService: [''],
          privacyPolicy: [''],
        }),
        mobile: this.fb.group({
          hero: this.fb.group({
            title: [''],
            description: [''],
            image: [''],
          }),
        }),
        onboarding: this.fb.group({
          title: [''],
          description: [''],
          sidePanel: this.fb.group({
            backgroundImage: [''],
            headerTitle: [''],
            footerTitle: [''],
            footerDescription: [''],
          }),
        }),
        checkout: this.fb.group({
          footer: this.fb.group({
            disclaimer: [''],
            copyright: [''],
          }),
        }),
        login: this.fb.group({
          sidePanel: this.fb.group({
            backgroundImage: [''],
            headline: [''],
            subheadline: [''],
            stats: this.fb.array<FormGroup>([]),
          }),
          mobileApp: this.fb.group({
            appStoreUrl: [''],
            googlePlayUrl: [''],
          }),
        }),
      });
    }
  }

  private ensureAllNestedGroups(): void {
    this.ensureMobileGroup();
    this.ensureFooterGroup();
    this.ensureAssetsGroup();
    this.ensureOnboardingGroup();
    this.ensureCheckoutGroup();
    this.ensureLoginGroup();
  }

  private ensureMobileGroup(): void {
    if (!this.internalFormGroup.get('mobile')) {
      this.internalFormGroup.addControl(
        'mobile',
        this.fb.group({
          hero: this.fb.group({
            title: [''],
            description: [''],
            image: [''],
          }),
        }),
      );
    }
  }

  private ensureFooterGroup(): void {
    if (!this.internalFormGroup.get('footer')) {
      this.internalFormGroup.addControl(
        'footer',
        this.fb.group({
          copyright: [''],
        }),
      );
    }
  }

  private ensureAssetsGroup(): void {
    if (!this.internalFormGroup.get('assets')) {
      this.internalFormGroup.addControl(
        'assets',
        this.fb.group({
          termsOfService: [''],
          privacyPolicy: [''],
        }),
      );
    }
  }

  private ensureOnboardingGroup(): void {
    if (!this.internalFormGroup.get('onboarding')) {
      this.internalFormGroup.addControl(
        'onboarding',
        this.fb.group({
          title: [''],
          description: [''],
          sidePanel: this.fb.group({
            backgroundImage: [''],
            headerTitle: [''],
            footerTitle: [''],
            footerDescription: [''],
          }),
        }),
      );
    } else {
      const onboarding = this.internalFormGroup.get('onboarding') as FormGroup;
      if (!onboarding.get('sidePanel')) {
        onboarding.addControl(
          'sidePanel',
          this.fb.group({
            backgroundImage: [''],
            headerTitle: [''],
            footerTitle: [''],
            footerDescription: [''],
          }),
        );
      }
    }
  }

  private ensureCheckoutGroup(): void {
    if (!this.internalFormGroup.get('checkout')) {
      this.internalFormGroup.addControl(
        'checkout',
        this.fb.group({
          footer: this.fb.group({
            disclaimer: [''],
            copyright: [''],
          }),
        }),
      );
    } else {
      const checkout = this.internalFormGroup.get('checkout') as FormGroup;
      if (!checkout.get('footer')) {
        checkout.addControl(
          'footer',
          this.fb.group({
            disclaimer: [''],
            copyright: [''],
          }),
        );
      }
    }
  }

  private ensureLoginGroup(): void {
    if (!this.internalFormGroup.get('login')) {
      this.internalFormGroup.addControl(
        'login',
        this.fb.group({
          sidePanel: this.fb.group({
            backgroundImage: [''],
            headline: [''],
            subheadline: [''],
            stats: this.fb.array<FormGroup>([]),
          }),
          mobileApp: this.fb.group({
            appStoreUrl: [''],
            googlePlayUrl: [''],
          }),
        }),
      );
      return;
    }

    const login = this.internalFormGroup.get('login') as FormGroup;
    if (!login.get('sidePanel')) {
      login.addControl(
        'sidePanel',
        this.fb.group({
          backgroundImage: [''],
          headline: [''],
          subheadline: [''],
          stats: this.fb.array<FormGroup>([]),
        }),
      );
    } else {
      const sidePanel = login.get('sidePanel') as FormGroup;
      if (!sidePanel.get('stats')) {
        sidePanel.addControl('stats', this.fb.array<FormGroup>([]));
      }
      if (!sidePanel.get('headline')) {
        sidePanel.addControl('headline', this.fb.control(''));
      }
      if (!sidePanel.get('subheadline')) {
        sidePanel.addControl('subheadline', this.fb.control(''));
      }
      if (!sidePanel.get('backgroundImage')) {
        sidePanel.addControl('backgroundImage', this.fb.control(''));
      }
    }

    if (!login.get('mobileApp')) {
      login.addControl(
        'mobileApp',
        this.fb.group({
          appStoreUrl: [''],
          googlePlayUrl: [''],
        }),
      );
    }
  }

  private setDefaultValues(): void {
    // Set default values if not already set
    if (
      this.defaultEmail &&
      !this.internalFormGroup.get('supportContactEmail')?.value
    ) {
      this.internalFormGroup.patchValue({
        supportContactEmail: this.defaultEmail,
      });
    }

    if (
      this.defaultWhatsapp &&
      !this.internalFormGroup.get('supportContactWhatsapp')?.value
    ) {
      this.internalFormGroup.patchValue({
        supportContactWhatsapp: this.defaultWhatsapp,
      });
    }

    if (
      this.defaultWebsite &&
      !this.internalFormGroup.get('institutionalWebsiteURL')?.value
    ) {
      this.internalFormGroup.patchValue({
        institutionalWebsiteURL: this.defaultWebsite,
      });
    }
  }
}
